import type {
  GetStaticPaths,
  GetStaticPropsContext,
  GetStaticPropsResult,
  InferGetStaticPropsType,
} from 'next';
import React from 'react';

import { JournalsPage } from '../../../components/Journals';
import Layout from '../../../components/Layout';
import { imageOgStaticLoader } from '../../../helpers/og-image';
import { FranchiseIndex, Franchises } from '../../../types/Franchises';
import { GlossaryItem } from '../../../types/Glossary';
import {
  fetchGlossary,
  GlossaryLink,
  mapGlossaryLinks,
  mapGlossaryTerms,
} from '../../../utils/glossary';
import { parseFranchiseContent } from '../../../utils/journals/franchises';
import shuffleArray from '../../../utils/shuffleArray';
import { getStoryblokPaginated, useStoryblok } from '../../../utils/storyblok';
import { pageTranslations } from '../../../utils/translations';

type PageProps = {
  locale: string;
  locales: string[];
  franchise: string;
  franchiseGlossaryTerms: GlossaryLink[];
  franchiseIndex: FranchiseIndex | null;
  franchisePage: Franchises;
  franchiseStories: Franchises[];
  journalStories: Franchises[];
  glossary: GlossaryItem[];
  preview: boolean;
  ogImageUrl: string;
};

export async function getStaticProps({
  locale = 'en',
  locales = ['en'],
  params,
  preview = false,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<PageProps>> {
  const franchise = params?.franchise as string;
  const slug = params?.slug as string;

  const [translations, stories, journals, glossary] = await Promise.all([
    pageTranslations(locale || 'en', ['insights', 'artists']),
    getStoryblokPaginated<Franchises>({
      locale,
      preview,
      starts_with: `insights/${franchise}/`,
    }),
    getStoryblokPaginated<Franchises>({
      locale,
      page: 1,
      per_page: 16,
      preview,
      is_startpage: false,
      starts_with: 'insights/',
      excluding_slugs: `insights/${franchise}/*`,
    }),
    fetchGlossary({ locale, preview }),
  ]);

  const page = stories.find((story) => story.slug === slug) || null;

  const index = stories.find((story) => story.is_startpage) as
    | FranchiseIndex
    | undefined;

  const list = stories.filter(
    (story) => !story.is_startpage && story.slug !== page?.slug,
  );

  if (page === null) {
    return {
      notFound: true,
    };
  }

  const franchiseGlossaryTerms = glossary.filter((term) =>
    (page.content.glossary || []).includes(term.uuid),
  );

  const franchisePage = parseFranchiseContent(page, glossary);
  const franchiseIndex = index ?? null;

  const ogImageUrl = await imageOgStaticLoader({
    overlineKey: franchiseIndex?.name,
    titleKey: franchisePage.content.title,
    imageSrc: franchisePage.content.poster?.filename
      ? franchisePage.content.poster.filename
      : 'https://a.storyblok.com/f/119968/4000x2667/846735f337/parra-atelier-2.jpg',
    imageWidth: 510,
    imageHeight: 510,
    isVideo: franchisePage.content.tag === 'video',
    locale,
  });

  return {
    props: {
      locale,
      locales,
      franchise,
      franchiseGlossaryTerms: mapGlossaryLinks(franchiseGlossaryTerms),
      franchiseIndex,
      franchisePage,
      franchiseStories: shuffleArray(list).slice(0, 4),
      glossary: mapGlossaryTerms(glossary),
      journalStories: shuffleArray(journals).slice(0, 4),
      preview,
      ogImageUrl,
      ...translations,
    },
  };
}

const Page = ({
  franchise,
  franchiseGlossaryTerms,
  franchiseIndex,
  franchisePage,
  franchiseStories,
  glossary,
  journalStories,
  locale,
  locales,
  ogImageUrl,
  preview,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { seo } = franchisePage.content;

  const page = useStoryblok(franchisePage, preview, locale);

  return (
    <Layout
      description={seo?.description}
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      title={seo?.title}
    >
      <JournalsPage
        franchise={franchise}
        franchiseGlossaryTerms={franchiseGlossaryTerms}
        franchiseIndex={franchiseIndex}
        franchisePage={page}
        franchiseStories={franchiseStories}
        glossary={glossary}
        journalStories={journalStories}
        locale={locale}
      />
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = () => ({
  paths: [],
  fallback: 'blocking',
});

export default Page;
